import { Modal } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { AssetService } from '~/app/services/asset/asset.service';
import { useAppToast } from '~/app/hooks/useAppToast';
import { usePermission } from '~/app/auth/hooks/usePermission';

type DeleteAssetTarget = {
  id: string;
  assetName: string;
  serialNumber?: string | null;
};

type UseDeleteAssetOptions = {
  /** When true, navigate to the assets list after a successful delete (detail page). */
  redirectToList?: boolean;
  organizationId: string;
};

export function useDeleteAsset(options: UseDeleteAssetOptions) {
  const { redirectToList = false, organizationId } = options;
  const { t } = useTranslation('assets');
  const { message } = useAppToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canDelete = hasPermission(PermissionKey.ASSETS_DELETE);

  const mutation = useMutation({
    mutationFn: (assetId: string) =>
      AssetService.deleteById(organizationId, assetId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assets', 'overview'] });
      message.success(t('delete.success'));
      if (redirectToList) {
        navigate('/assets');
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : t('delete.error');
      message.error(errorMessage);
    },
  });

  const confirmDelete = (asset: DeleteAssetTarget) => {
    if (!canDelete) {
      return;
    }

    Modal.confirm({
      title: t('delete.confirmTitle'),
      content: t('delete.confirmContent', {
        name: asset.assetName,
      }),
      okText: t('delete.confirmOk'),
      cancelText: t('delete.confirmCancel'),
      okButtonProps: { danger: true, loading: mutation.isPending },
      onOk: () => mutation.mutateAsync(asset.id),
    });
  };

  return {
    canDelete,
    confirmDelete,
    isDeleting: mutation.isPending,
  };
}
